import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Download, GraduationCap, PlayCircle, X } from "lucide-react";
import TrainingQuiz from "./TrainingQuiz";

export const VIDEOS = {
  burn: { slug: "cash-burn-training", title: "Cash Burn & Runway walkthrough", quiz: "burn" },
  tax: { slug: "tax-savings-training", title: "Tax Savings walkthrough", quiz: "tax" },
  debt: { slug: "debt-reduction-training", title: "Debt Reduction walkthrough", quiz: "debt" },
  goals: { slug: "client-goals-training", title: "Client Goals walkthrough", quiz: "goals" },
  client: { slug: "client-outlook", title: "Your financial outlook in 60 seconds", eyebrow: "Your outlook" },
};
const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export function TrainingVideoButton({ video = "burn", className = "", testid, label = "Watch training video", autoOpen = false }) {
  const [open, setOpen] = useState(autoOpen);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`text-sm px-3 py-2 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1.5 transition-colors ${className}`}
        style={{ borderColor: "var(--ll-border)" }}
        data-testid={testid || `${video}-training-btn`}
      >
        <PlayCircle size={15} /> {label}
      </button>
      {open && <TrainingVideoModal video={video} onClose={() => setOpen(false)} />}
    </>
  );
}

export function TrainingVideoPlayer({ video = "burn", autoPlay = false, onEnded }) {
  const { slug, quiz } = VIDEOS[video];
  const src = `/training/${slug}.mp4`;
  const ref = useRef(null);
  const sent = useRef(false);
  const onTime = (e) => {
    const el = e.currentTarget;
    setT(el.currentTime);
    if (quiz && !sent.current && el.duration && el.currentTime / el.duration >= 0.9) {
      sent.current = true;
      api.post(`/training/watched/${quiz}`).catch(() => {});
    }
  };
  const [meta, setMeta] = useState({ chapters: [], duration: 0 });
  const [t, setT] = useState(0);
  useEffect(() => { fetch(`/training/${slug}.json`).then((r) => r.json()).then(setMeta).catch(() => {}); }, [slug]);
  const current = [...meta.chapters].reverse().find((c) => t >= c.start - 0.2);
  const seek = (s) => {
    if (!ref.current) return;
    ref.current.currentTime = s;
    ref.current.play()?.catch(() => {});
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid={`training-video-player-${video}`}>
      <div className="lg:col-span-9 bg-black">
        <video ref={ref} controls autoPlay={autoPlay} preload="metadata" playsInline onEnded={onEnded}
          onTimeUpdate={onTime} className="w-full h-auto block" data-testid={`training-video-${video}`}>
          <source src={src} type="video/mp4" />
          <source src={`/training/${slug}.webm`} type="video/webm" />
          Your browser can't play this video — use the Download MP4 link.
        </video>
      </div>
      <div className="lg:col-span-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)] mb-2">Chapters · {fmt(meta.duration || 0)}</div>
        <ol className="space-y-0.5" data-testid={`training-chapters-${video}`}>
          {meta.chapters.map((c, i) => (
            <li key={c.key}>
              <button onClick={() => seek(c.start)}
                className={`w-full text-left px-2.5 py-1.5 text-sm flex justify-between gap-2 transition-colors ${current?.key === c.key ? "bg-[var(--ll-mint)] font-medium" : "hover:bg-[var(--ll-mint)]"}`}
                data-testid={`training-chapter-${video}-${c.key}`}>
                <span>{i + 1}. {c.title}</span>
                <span className="font-mono-fin text-xs text-[var(--ll-ink-muted)]">{fmt(c.start)}</span>
              </button>
            </li>
          ))}
        </ol>
        <a href={src} download={`${slug}.mp4`} className="mt-3 text-xs inline-flex items-center gap-1 hover:underline" data-testid={`training-download-${video}`}>
          <Download size={12} /> Download MP4
        </a>
      </div>
    </div>
  );
}

export default function TrainingVideoModal({ video = "burn", onClose }) {
  const v = VIDEOS[video];
  const [quiz, setQuiz] = useState(false);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 grid place-items-center p-4 overflow-y-auto" onClick={onClose} data-testid="training-video-modal">
      <div className="bg-white w-full max-w-6xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center justify-between gap-3 bg-[var(--ll-ink)] text-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-70">{v.eyebrow || "Training · Advisory"}</div>
            <div className="font-display font-bold text-sm">{v.title}</div>
          </div>
          <div className="flex items-center gap-3">
            {v.quiz && !quiz && (
              <button onClick={() => setQuiz(true)} className="text-xs px-3 py-1.5 bg-white text-[var(--ll-ink)] inline-flex items-center gap-1.5 hover:opacity-90" data-testid="training-take-quiz-btn">
                <GraduationCap size={14} /> Take the 3-question check
              </button>
            )}
            <button onClick={onClose} aria-label="Close" data-testid="training-video-close"><X size={18} /></button>
          </div>
        </div>
        <div className="p-4">
          {quiz ? <TrainingQuiz topic={v.quiz} onBack={() => setQuiz(false)} />
            : <TrainingVideoPlayer video={video} autoPlay onEnded={() => v.quiz && setQuiz(true)} />}
        </div>
      </div>
    </div>
  );
}
